import React, { useState, useRef, useEffect } from 'react';

// Компонент анимированной подсказки
const AnimatedTooltip = ({ step, isVisible, position, onMouseLeave }) => {
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      // Плавное появление с задержкой
      const timer = setTimeout(() => {
        tooltipRef.current.style.opacity = '1';
        tooltipRef.current.style.transform = 'translateY(0) scale(1)';
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!isVisible || !step) return null;

  // Определяем, с какой стороны показывается подсказка
  const isOnRight = position.isOnRight !== undefined ? position.isOnRight : position.x > 150;

  return (
    <div
      ref={tooltipRef}
      className="animated-tooltip"
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        opacity: 0,
        transform: 'translateY(10px) scale(0.95)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 10000,
        pointerEvents: 'none'
      }}
      onMouseLeave={onMouseLeave}
    >
      <div className={`tooltip-content ${isOnRight ? 'tooltip-right' : 'tooltip-left'}`}>
        <div className="tooltip-header">
          <span className="tooltip-icon">📋</span>
          <span className="tooltip-title">Детали шага</span>
        </div>
        <div className="tooltip-body">
          <div className="tooltip-row">
            <span className="tooltip-label">Кто совершил действие:</span>
            <span className="tooltip-value">{step.category}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Действие:</span>
            <span className="tooltip-value">{step.product}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Способ:</span>
            <span className="tooltip-value">{step.action}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Код:</span>
            <span className="tooltip-code">{step.code}</span>
          </div>
        </div>
        <div className={`tooltip-arrow ${isOnRight ? 'arrow-left' : 'arrow-right'}`}></div>
      </div>
    </div>
  );
};

const DraggableSteps = ({ steps, onDragEnd, onRemoveStep, isLocked = false }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [tooltipState, setTooltipState] = useState({
    isVisible: false,
    step: null,
    position: { x: 0, y: 0 }
  });
  const dragRef = useRef(null);

  if (!steps || steps.length === 0) {
    return (
      <div className="steps-container">
        <p style={{ color: '#666', fontStyle: 'italic' }}>Шаги процесса появятся здесь</p>
      </div>
    );
  }

  const handleDragStart = (e, index) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }
    
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    
    // Добавляем класс для анимации
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);

    // Добавляем глобальный обработчик для предотвращения выделения текста
    document.body.style.userSelect = 'none';
  };

  const handleDragOver = (e, index) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Создаем новый массив шагов
    const newSteps = Array.from(steps);
    const [removed] = newSteps.splice(draggedIndex, 1);
    newSteps.splice(dropIndex, 0, removed);

    // Вызываем callback для обновления состояния
    onDragEnd({
      source: { index: draggedIndex },
      destination: { index: dropIndex }
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    // Убираем класс анимации
    const draggingElement = document.querySelector('.step-item.dragging');
    if (draggingElement) {
      draggingElement.classList.remove('dragging');
    }

    // Восстанавливаем выделение текста
    document.body.style.userSelect = '';
  };

  const handleRemoveStep = (index) => {
    if (isLocked) {
      return;
    }
    onRemoveStep(index);
  };

  // Обработчики для подсказки
  const handleMouseEnter = (e, step) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = e.currentTarget.closest('.steps-container').getBoundingClientRect();
    
    // Вычисляем позицию подсказки
    const tooltipWidth = 280; // примерная ширина подсказки
    const tooltipHeight = 200; // примерная высота подсказки
    
    // Позиционируем подсказку справа от элемента по центру
    let x = rect.right - containerRect.left + 15; // справа от элемента
    let y = rect.top - containerRect.top + rect.height / 2 - tooltipHeight / 2; // по центру по вертикали
    let isOnRight = true; // флаг для определения стороны
    
    // Проверяем, чтобы подсказка не выходила за границы контейнера
    if (x + tooltipWidth > containerRect.width) {
      // Если справа не помещается, показываем слева
      x = rect.left - containerRect.left - tooltipWidth - 15;
      isOnRight = false;
    }
    
    if (y < 0) y = 10; // если сверху не помещается
    if (y + tooltipHeight > containerRect.height) y = containerRect.height - tooltipHeight - 10; // если снизу не помещается
    
    setTooltipState({
      isVisible: true,
      step,
      position: { x, y, isOnRight }
    });
  };

  const handleMouseLeave = () => {
    setTooltipState(prev => ({ ...prev, isVisible: false }));
  };

  return (
    <div className="steps-container" style={{ position: 'relative' }}>
      {steps.map((step, idx) => (
        <div
          key={`step-${idx}`}
          className={`step-item ${draggedIndex === idx ? 'dragging' : ''} ${dragOverIndex === idx ? 'drag-over' : ''} ${isLocked ? 'locked' : ''}`}
          draggable={!isLocked}
          onDragStart={(e) => handleDragStart(e, idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, idx)}
          onDragEnd={handleDragEnd}
          onMouseEnter={(e) => handleMouseEnter(e, step)}
          onMouseLeave={handleMouseLeave}
          style={{
            opacity: draggedIndex === idx ? 0.5 : (isLocked ? 0.7 : 1),
            transform: draggedIndex === idx ? 'rotate(5deg) scale(1.05)' : 'none',
            zIndex: draggedIndex === idx ? 1000 : 'auto',
            cursor: isLocked ? 'not-allowed' : 'grab'
          }}
        >
          <div className="step-content">
            <span 
              className="drag-handle" 
              title={isLocked ? "Редактирование заблокировано" : "Перетащить"}
              style={{ cursor: isLocked ? 'not-allowed' : 'grab' }}
            >
              {isLocked ? '🔒' : '⋮⋮'}
            </span>
            <span className="step-num">{idx + 1}.</span> 
            <span className="step-code">{step.code}</span>
          </div>
          {!isLocked && (
          <button 
            className="remove-step-btn" 
              onClick={() => handleRemoveStep(idx)}
            title="Удалить шаг"
          >
            ✕
          </button>
          )}
        </div>
      ))}
      
      {/* Анимированная подсказка */}
      <AnimatedTooltip
        step={tooltipState.step}
        isVisible={tooltipState.isVisible}
        position={tooltipState.position}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
};

export default DraggableSteps; 